import { Pencil, X } from "lucide-react";
import { T } from "../lib/theme";
import { fmtDate } from "../lib/dates";
import { ROLE_LABEL, type HistoryEntry, type MemberSummary } from "../lib/types";
import { Sheet } from "./ui";

/** One member's talks and prayers. Reached from both Up Next and Members, so the
 *  same history is available wherever you tap a name. */
export function HistorySheet({
  m,
  history,
  onClose,
  onEdit,
}: {
  m: MemberSummary;
  history: HistoryEntry[];
  onClose: () => void;
  /** Only the Members screen passes this — it switches to the edit sheet. */
  onEdit?: () => void;
}) {
  return (
    <Sheet onClose={onClose}>
      <div style={{ padding: "20px 20px 8px", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <h2 className="serif" style={{ margin: 0, fontSize: 24, fontWeight: 500 }}>
              {m.last_name}, {m.first_name}
            </h2>
            <div style={{ fontSize: 12.5, color: T.muted, marginTop: 4 }}>
              {m.status === "moved_out" ? "Moved out · " : ""}
              {m.speaker_type === "youth" ? "Youth speaker" : "Adult speaker"} · {m.speak_count} talk
              {m.speak_count === 1 ? "" : "s"} · {m.prayer_count} prayer{m.prayer_count === 1 ? "" : "s"}
            </div>
          </div>

          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
            {onEdit && (
              <button onClick={onEdit} aria-label="Edit member" style={{ color: T.accent, padding: 4, display: "flex" }}>
                <Pencil size={18} />
              </button>
            )}
            <button onClick={onClose} aria-label="Close" style={{ color: T.muted, padding: 4, display: "flex" }}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 22, margin: "16px 0 14px" }}>
          <div>
            <div className="eyebrow">Last spoke</div>
            <div
              className="tnum serif"
              style={{ fontSize: 19, marginTop: 2, color: m.last_spoke_date ? T.ink : T.brass }}
            >
              {m.last_spoke_date ? fmtDate(m.last_spoke_date) : "Never"}
            </div>
          </div>
          <div>
            <div className="eyebrow">Last prayer</div>
            <div
              className="tnum serif"
              style={{ fontSize: 19, marginTop: 2, color: m.last_prayer_date ? T.ink : T.faint }}
            >
              {m.last_prayer_date ? fmtDate(m.last_prayer_date) : "Never"}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: "6px 20px 26px" }}>
        <div className="eyebrow" style={{ margin: "12px 0 8px" }}>
          History
        </div>

        {history.length === 0 && (
          <div style={{ fontSize: 14, color: T.muted, padding: "18px 0", lineHeight: 1.5 }}>
            Nothing recorded yet. This person is a good candidate.
          </div>
        )}

        {history.map((h, i) => (
          <div
            key={`${h.date}-${h.role}`}
            style={{
              display: "flex",
              gap: 14,
              padding: "10px 0",
              borderBottom: i < history.length - 1 ? `1px solid ${T.line}` : "none",
            }}
          >
            <div className="tnum" style={{ fontSize: 13, color: T.muted, width: 96, flexShrink: 0 }}>
              {fmtDate(h.date)}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 500 }}>{ROLE_LABEL[h.role]}</div>
              {h.topic && (
                <div className="serif" style={{ fontSize: 14.5, color: T.muted, marginTop: 1 }}>
                  {h.topic}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Sheet>
  );
}
